import { Fragment, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import SearchField from "../../DesignSystem/Inputs/SearchField";
import Pagination from "../../DesignSystem/Pagination/Pagination";
import { useUserEmail } from "../../SignIn/UserEmailContext";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import { loadSellingAdviserIds, subscribeFees } from "./collectFeesService";
import { FEATURE_FLAGS } from "../../../constants/featureFlags";
import "../../../css/Finance/CollectFees.css";

const FEES_PER_PAGE = 10;
const ADMIN_PERMISSIONS = ["administrator", "finance admin", "admin"];

const text = (value) => String(value ?? "").trim();
const money = (amount, currency = "GBP") => {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "-";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: text(currency) || "GBP",
    }).format(value);
  } catch {
    return `${text(currency) || "GBP"} ${value.toFixed(2)}`;
  }
};
const date = (value) => {
  const raw = typeof value?.toDate === "function" ? value.toDate() : value;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format("DD MMM YYYY, HH:mm") : "-";
};
const timestamp = (value) => {
  const raw = typeof value?.toDate === "function" ? value.toDate() : value;
  const parsed = dayjs(raw).valueOf();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatPlanId = (value) => {
  const id = text(value);
  if (!id) return "";
  return /^IOB/i.test(id) ? id.toUpperCase() : `IOB${id}`;
};

const getPlanDetails = (fee) => {
  const payloadPlans = Array.isArray(fee?.payload?.plans)
    ? fee.payload.plans
    : [];
  const planIds = fee.planIds?.length
    ? fee.planIds
    : payloadPlans.map((plan) => plan?.id);

  return planIds.map(formatPlanId).filter(Boolean).join(", ") || "-";
};

const getClientDetails = (fee) => {
  const enrichedClients = Array.isArray(fee?.clientDetails)
    ? fee.clientDetails
    : [];
  const payloadClients = Array.isArray(fee?.payload?.clients)
    ? fee.payload.clients
    : [];
  const clients = enrichedClients.length ? enrichedClients : payloadClients;
  const clientValues = clients.map(
    (client) =>
      client?.name ||
      [
        client?.firstName || client?.forename,
        client?.lastName || client?.surname,
      ]
        .filter(Boolean)
        .join(" ") ||
      client?.id,
  );
  const values = clientValues.some(Boolean)
    ? clientValues
    : fee.clientIds || [];
  return values.map(text).filter(Boolean).join(", ") || "-";
};

const getSplitName = (split) =>
  split?.name ||
  split?.splitName ||
  split?.description ||
  split?.recipientName ||
  split?.groupName ||
  split?.recipientType ||
  "Split";

const formatLabel = (value) =>
  text(value)
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (letter) => letter.toUpperCase());

const DetailValue = ({ value }) => {
  if (typeof value?.toDate === "function") return date(value);
  if (value === null || value === undefined || value === "") {
    return <span className="collect-fee-muted">Not available</span>;
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string" && /^https?:\/\//i.test(value)) {
    return (
      <a href={value} target="_blank" rel="noreferrer">
        Open in Intelliflo
      </a>
    );
  }
  if (Array.isArray(value)) {
    if (!value.length) return <span className="collect-fee-muted">None</span>;
    return (
      <div className="collect-fee-nested-list">
        {value.map((item, index) => (
          <div
            className="collect-fee-nested-item"
            key={item?.id || item?.splitId || index}
          >
            <DetailValue value={item} />
          </div>
        ))}
      </div>
    );
  }
  if (typeof value === "object") {
    return (
      <div className="collect-fee-fields">
        {Object.entries(value).map(([key, item]) => (
          <div className="collect-fee-field" key={key}>
            <div className="collect-fee-field__label">{formatLabel(key)}</div>
            <div className="collect-fee-field__value">
              <DetailValue value={item} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  return String(value);
};

const CompleteFeeDetails = ({ fee }) => {
  const { payload, calculatedSplits, ...feeRecord } = fee;
  return (
    <div className="collect-fee-complete">
      <section className="collect-fee-section">
        <h5>Fee record</h5>
        <DetailValue value={feeRecord} />
      </section>
      {calculatedSplits && (
        <section className="collect-fee-section">
          <h5>Calculated splits</h5>
          <DetailValue value={calculatedSplits} />
        </section>
      )}
      {payload && (
        <section className="collect-fee-section">
          <h5>Intelliflo event payload</h5>
          <DetailValue value={payload} />
        </section>
      )}
    </div>
  );
};

const FeesPaidPage = () => {
  const { userEmail } = useUserEmail();
  const { userPermissions = [] } = useUserPermissions();
  const isAdmin = userPermissions.some((permission) =>
    ADMIN_PERMISSIONS.includes(text(permission).toLowerCase()),
  );
  const [fees, setFees] = useState([]);
  const [sellingAdviserIds, setSellingAdviserIds] = useState([]);
  const [idsLoading, setIdsLoading] = useState(!isAdmin);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedFeeId, setExpandedFeeId] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      setSellingAdviserIds([]);
      setIdsLoading(false);
      return;
    }

    let cancelled = false;
    setIdsLoading(true);
    loadSellingAdviserIds(userEmail)
      .then((ids) => {
        if (!cancelled) setSellingAdviserIds(ids);
      })
      .catch(() => {
        if (!cancelled)
          setError("We couldn't load your Intelliflo adviser IDs.");
      })
      .finally(() => {
        if (!cancelled) setIdsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, userEmail]);

  useEffect(() => {
    if (idsLoading) return undefined;
    setLoading(true);
    return subscribeFees(
      { isAdmin, sellingAdviserIds },
      (items) => {
        setFees(items);
        setLoading(false);
        setError("");
      },
      () => {
        setError("We couldn't load the fee records.");
        setLoading(false);
      },
    );
  }, [idsLoading, isAdmin, sellingAdviserIds]);

  const filteredFees = useMemo(() => {
    const queryText = searchTerm.trim().toLowerCase();
    return [...fees]
      .filter(
        (fee) =>
          !queryText ||
          [
            fee.feeId,
            fee.sequentialRef,
            fee.description,
            fee.feeCode,
            fee.adviserEmail,
            fee.sellingAdviserId,
            ...(fee.planIds || []),
            ...(fee.clientIds || []),
          ].some((value) => text(value).toLowerCase().includes(queryText)),
      )
      .sort(
        (left, right) =>
          timestamp(right.eventTimestamp || right.updatedAt) -
          timestamp(left.eventTimestamp || left.updatedAt),
      );
  }, [fees, searchTerm]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredFees.length / FEES_PER_PAGE),
  );
  const page = Math.min(currentPage, totalPages);
  const visibleFees = filteredFees.slice(
    (page - 1) * FEES_PER_PAGE,
    page * FEES_PER_PAGE,
  );

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  return (
    <div className="Main-Page bg-lavender-web">
      <Helmet>
        <title>Fee Paid</title>
        <meta
          name="description"
          content="View Intelliflo fee records and adviser values."
        />
      </Helmet>
      <Sidebar />
      <main className="Main-Container table-contained-page table-scroll-page column gap-s">
        <Breadcrumb
          items={[
            { label: "Finance", to: "/Department/Finance" },
            { label: "Fee Paid" },
          ]}
        />
        <header className="collect-fees-header">
          <div className="fit-column gap-xs">
            <h3 className="FP_H3">Fee Paid</h3>
            <div className="FP_Help_Text">
              {isAdmin
                ? "View all fees raised in Intelliflo."
                : "View fees assigned to your Intelliflo adviser IDs."}
            </div>
          </div>
          <SearchField
            value={searchTerm}
            onChange={(event) => handleSearch(event.target.value)}
            placeholder="Search fees, plans or clients"
          />
        </header>

        {error && (
          <div className="FP_Help_Text text-error" role="alert">
            {error}
          </div>
        )}
        {!isAdmin && !idsLoading && !sellingAdviserIds.length && !error && (
          <div className="FP_Help_Text primary" role="status">
            No Intelliflo adviser IDs are saved against your account.
          </div>
        )}

        <section className="bg-lavender-web card-corner-radius table-scroll-card">
          <div className="table-scroll-container table-rows-scroll-container">
            <table>
              <thead>
                <tr>
                  <th className="FP_Help_Text_Semibold uppercase white-text">
                    Fee
                  </th>
                  <th className="FP_Help_Text_Semibold uppercase white-text">
                    Adviser, client &amp; plan
                  </th>
                  <th className="FP_Help_Text_Semibold uppercase white-text">
                    Fee value
                  </th>
                  <th className="FP_Help_Text_Semibold uppercase white-text">
                    Adviser value
                  </th>
                  <th className="FP_Help_Text_Semibold uppercase white-text">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading || idsLoading ? (
                  <tr>
                    <td colSpan="5" className="FP_Help_Text primary">
                      Loading fees...
                    </td>
                  </tr>
                ) : visibleFees.length ? (
                  visibleFees.map((fee, index) => (
                    <Fragment key={fee.id}>
                      <tr>
                        <td>
                          <div className="row gap-xs">
                            <span className="FP_Help_Text_Semibold primary">
                              {(page - 1) * FEES_PER_PAGE + index + 1}.
                            </span>
                            <span className="fit-column gap-xs">
                              <strong className="FP_Help_Text_Semibold primary">
                                {fee.description ||
                                  fee.feeTypeName ||
                                  `Fee ${fee.feeId}`}
                              </strong>
                              <span className="FP_Help_Text">
                                {fee.sequentialRef || fee.feeCode || fee.feeId}
                              </span>
                              <span className="FP_Help_Text">
                                Updated: {date(fee.updatedAt)}
                              </span>
                          {FEATURE_FLAGS.showFeesPaidDetails && (
                              <button
                                type="button"
                                className="collect-fees-details-button FP_Help_Text_Semibold"
                                aria-expanded={expandedFeeId === fee.id}
                                onClick={() =>
                                  setExpandedFeeId((current) =>
                                    current === fee.id ? null : fee.id,
                                  )
                                }
                              >
                                {expandedFeeId === fee.id
                                  ? "Hide all details"
                                  : "View all details"}
                              </button>
                            )}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="fit-column gap-xs">
                            <strong className="FP_Help_Text_Semibold primary">
                              {fee.adviserEmail ||
                                `IO adviser ${fee.sellingAdviserId || "-"}`}
                            </strong>
                            <span className="FP_Help_Text">
                              IO ID: {fee.sellingAdviserId || "-"}
                            </span>
                            <span className="FP_Help_Text">
                              Plan: {getPlanDetails(fee)}
                            </span>
                            <span className="FP_Help_Text">
                              Client: {getClientDetails(fee)}
                            </span>
                            <span className="FP_Help_Text">
                              Product: {fee.productName || "-"}
                            </span>
                            <span className="FP_Help_Text">
                              Provider: {fee.productProviderName || "-"}
                            </span>
                            <span className="FP_Help_Text">
                              Policy number: {fee.policyNumber || "-"}
                            </span>
                            <span className="FP_Help_Text">
                              Plan status:{" "}
                              {fee.planSystemStatus ||
                                fee.planCurrentStatus ||
                                "-"}
                            </span>
                          </div>
                        </td>
                        <td className="FP_Help_Text_Semibold">
                          <div className="fit-column gap-xs">
                            <strong>
                              {money(fee.netAmount, fee.currency)}
                            </strong>
                            <span className="FP_Help_Text">
                              Invoice date: {date(fee.invoiceDate)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="fit-column gap-xs">
                            <strong className="FP_Help_Text_Semibold primary">
                              {money(fee.adviserValue, fee.currency)}
                            </strong>
                            <span className="FP_Help_Text">
                              {Number.isFinite(Number(fee.adviserPercentage))
                                ? `Adviser: ${fee.adviserPercentage}%`
                                : "-"}
                            </span>
                            {Array.isArray(fee.calculatedSplits) &&
                            fee.calculatedSplits.length ? (
                              fee.calculatedSplits.map((split, index) => (
                                <span
                                  className="FP_Help_Text"
                                  key={split.splitId || index}
                                >
                                  {getSplitName(split)}:{" "}
                                  {Number.isFinite(Number(split.percentage))
                                    ? `${split.percentage}%`
                                    : "-"}
                                </span>
                              ))
                            ) : (
                              <span className="FP_Help_Text">
                                Introducer:{" "}
                                {Number.isFinite(
                                  Number(fee.introducerPercentage),
                                )
                                  ? `${fee.introducerPercentage}%`
                                  : "-"}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="FP_Help_Text_Semibold">
                          {fee.status || fee.eventType || "-"}
                        </td>
                      </tr>
                    {FEATURE_FLAGS.showFeesPaidDetails &&
                        expandedFeeId === fee.id && (
                          <tr className="collect-fees-details-row">
                            <td colSpan="5">
                              <div className="collect-fees-document">
                                <div className="FP_Content_Semibold primary">
                                  Complete fee document
                                </div>
                                <CompleteFeeDetails fee={fee} />
                              </div>
                            </td>
                          </tr>
                        )}
                    </Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="FP_Help_Text primary">
                      No fee records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </main>
    </div>
  );
};

export default FeesPaidPage;
